import { useLocation, useParams } from "react-router-dom";
import { useFormFields } from "../hooks/useFormFields";
import Loader from "../components/Loader";
import TextField from "../components/TextField";
import { ReactComponent as NoData } from "../assets/no_data.svg";
import { MdKeyboardBackspace } from "react-icons/md";
import usePaperlessNavigate from "../hooks/usePaperlessNavigate";
import FormLayout from "../components/FormLayout";
import { useAtom } from "jotai";
import { createFormAtom } from "../atoms/createFormAtom";
import { useEffect, useMemo } from "react";
import NetworkError from "../components/NetworkError";
import { useFormNotes } from "../hooks/useFormNotes";
import TermsAndConditions from "../components/TermsAndConditions";
import QRCode from "react-qr-code";

const FormView = () => {
  const { formId } = useParams();
  const { data, isError, error, isLoading, isSuccess } = useFormFields(formId);
  const { data: notes, isLoading: isLoadingNotes, isSuccess: isLoadedNotes } = useFormNotes(formId);
  const { paperlessNavigate } = usePaperlessNavigate();
  const location = useLocation();
  const [formAtom, setFormAtom] = useAtom(createFormAtom);

  // --- Read query params (title + pNumber/branch) ---------------------------
  const searchParams = new URLSearchParams(location.search);
  const titleFromQuery = searchParams.get("title");
  const pFromQuery = searchParams.get("pNumber") || searchParams.get("p"); // accept ?pNumber= or ?p=
  const pageTitle = location?.state || titleFromQuery || "Form";

  // --- Ensure formTypeId is set --------------------------------------------
  useEffect(() => {
    if (!formId) return;
    setFormAtom((prev) => ({ ...prev, formTypeId: Number(formId) }));
  }, [formId, setFormAtom]);

  // --- Initialize pNumber into atom/localStorage ----------------------------
  useEffect(() => {
    // Priority: query param -> atom -> localStorage
    const atomP = formAtom?.pNumber;
    const localP = localStorage.getItem("selectedBranchId");

    if (pFromQuery) {
      // If URL carries the branch, persist it
      localStorage.setItem("selectedBranchId", String(pFromQuery));
      setFormAtom((prev) => ({ ...prev, pNumber: Number(pFromQuery) || 0 }));
      return;
    }

    // If atom not set but localStorage has branch, hydrate atom from localStorage
    if ((atomP == null || atomP === 0) && localP) {
      setFormAtom((prev) => ({ ...prev, pNumber: Number(localP) || 0 }));
    }
  }, [pFromQuery, setFormAtom]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Build a shareable URL that always includes ?title=... & ?pNumber=... -
  const url = new URL(window.location.href);

  // Ensure title is present
  if (!url.searchParams.get("title") && pageTitle) {
    url.searchParams.set("title", pageTitle);
  }

  // Ensure pNumber is present in the QR URL:
  // take from: query -> atom -> localStorage
  const pForUrl =
    pFromQuery ??
    (formAtom?.pNumber != null && formAtom.pNumber !== 0 ? String(formAtom.pNumber) : null) ??
    localStorage.getItem("selectedBranchId");

  if (pForUrl && !url.searchParams.get("pNumber")) {
    url.searchParams.set("pNumber", String(pForUrl));
  }

  const currentUrl = url.toString();

  // ---------- Sections ----------
  const allSections = data ?? [];
  const userSections = useMemo(
    () => allSections.filter((fs) => !fs.isOfficial && fs.activeStatus),
    [allSections]
  );

  // ---------- Normalization ----------
  const normalize = (s) =>
    (s ?? "")
      .normalize("NFKC")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\u00A0/g, " ")
      .replace(/\u2044/g, "/")
      .replace(/[’]/g, "'")
      .replace(/\s+/g, " ")
      .replace(/\s*([()\/.\-+])\s*/g, "$1")
      .trim()
      .toLowerCase();

  // ---------- Label -> FieldId ----------
  const labelToId = useMemo(() => {
    const m = new Map();
    for (const sec of allSections) {
      for (const f of sec.formFields || []) {
        m.set(normalize(f.label), String(f.formFieldId));
      }
    }
    return m;
  }, [allSections]);

  // ---------- Formula map ----------
  const formulaMap = useMemo(() => {
    const m = new Map();
    for (const sec of allSections) {
      for (const f of sec.formFields || []) {
        if (f?.isCalculated && (f.calculationFormulaeDependents || f.calculationFormula)) {
          const raw = String(f.calculationFormulaeDependents || f.calculationFormula);
          const rhs = raw.split("=").pop().trim();

          const rhsWithIds = rhs.replace(/\[([^\]]+)\]/g, (_, token) => {
            const t = String(token).trim();
            if (/^\d+$/.test(t)) return `[${t}]`;
            const id = labelToId.get(normalize(t));
            return id ? `[${id}]` : `[0]`;
          });

          m.set(String(f.formFieldId), rhsWithIds);
        }
      }
    }
    return m;
  }, [allSections, labelToId]);

  // ---------- Raw values by id ----------
  const rawById = useMemo(() => {
    const m = new Map();
    for (const v of formAtom?.formValues || []) {
      m.set(String(v.formFieldId), v.formData ?? "");
    }
    console.log('formAtom?.formValues',m);

    return m;
  }, [formAtom?.formValues]);

  // ---------- Numeric value map ----------
  const numericValueMap = useMemo(() => {
    const o = {};
    for (const v of formAtom?.formValues || []) {
      
      const n = Number(v.formData);
      if (Number.isFinite(n)){
        console.log('isfinite',n);
         o[String(v.formFieldId)] = n;
        }
    }
  

    return o;
  }, [formAtom?.formValues]);

  // ---------- Evaluator ----------
  function evaluateField(fieldId, ctx, memo = {}, stack = new Set()) {
    const key = String(fieldId);
    if (Object.prototype.hasOwnProperty.call(memo, key)) return memo[key];
    if (!ctx.formulaMap.has(key)) {
      const v = ctx.valueMap[key];
      const n = Number.isFinite(v) ? v : 0;
      memo[key] = n;
      return n;
    }
    if (stack.has(key)) throw new Error(`Circular formula reference at [${key}]`);
    stack.add(key);

    const expr = ctx.formulaMap.get(key);
    const replaced = expr.replace(/\[([^\]]+)\]/g, (_, dep) => {
      const depId = String(dep).trim();
      const depVal = evaluateField(depId, ctx, memo, stack);
      return String(depVal);
    });

    if (!/^[0-9+\-*/%.() \t\n\r]*$/.test(replaced)) {
      throw new Error(`Illegal characters in expression for field [${key}]`);
    }

    const result = Function(`"use strict"; return (${replaced});`)();
    const finalNum = Number.isFinite(result) ? result : 0;

    stack.delete(key);
    memo[key] = finalNum;
    ctx.valueMap[key] = finalNum;
    return finalNum;
  }

  // ---------- Compute all calculated values ----------
  const computedNumbersById = useMemo(() => {
   console.log("Recomputing computedNumbersById",numericValueMap,formulaMap);
    const ctx = { valueMap: { ...numericValueMap }, formulaMap };
    const memo = {};
    for (const fid of formulaMap.keys()) {
      try {
        evaluateField(fid, ctx, memo);
      } catch {
        ctx.valueMap[String(fid)] = 0;
      }
    }
    console.log("Computed values:", ctx.valueMap);
    return ctx.valueMap;
  }, [numericValueMap, formulaMap]);

  // ---------- Sync computed values back ----------
  const computedIdList = useMemo(
    () => Array.from(formulaMap.keys()).map(String).sort(),
    [formulaMap]
  );

  useEffect(() => {
    if (computedIdList.length === 0) return;
    setFormAtom((prev) => {
      const prevVals = prev?.formValues ?? [];
      const prevMap = new Map(prevVals.map((v) => [String(v.formFieldId), String(v.formData ?? "")]));
      const computedEntries = computedIdList.map((id) => ({
        formFieldId: String(id),
        formData: String(computedNumbersById[id] ?? 0),
      }));
      let changed = false;
      for (const { formFieldId, formData } of computedEntries) {
        if (prevMap.get(String(formFieldId)) !== formData) {
          changed = true;
          break;
        }
      }
      const hasAllComputed = computedEntries.every((e) => prevMap.has(String(e.formFieldId)));
      if (!changed && hasAllComputed) return prev;

      const computedSet = new Set(computedIdList);
      const nextValues = [
        ...prevVals.filter((v) => !computedSet.has(String(v.formFieldId))),
        ...computedEntries,
      ];
      return { ...prev, formValues: nextValues };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFormAtom, computedIdList.join("|"), JSON.stringify(computedNumbersById)]);

  // ---------- Handle changes ----------
  const handleChange = (e) => {
    console.log("Handling change", e.target.id, e.target.value);
    const { id, type, checked, value } = e.target;
    const formValue = type === "checkbox" ? JSON.stringify(checked) : value;
    const fid = String(id);
    setFormAtom((prev) => ({
      ...prev,
      formValues: [
        ...prev.formValues.filter((fv) => String(fv.formFieldId) !== fid),
        { formFieldId: fid, formData: formValue },
      ],
    }));
   

  };

  // Clear values if form meta changes
  useEffect(() => {
    if (!formAtom.formTypeId || !formAtom.categoryId) return;
    setFormAtom({ ...formAtom, formValues: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formAtom.formTypeId, formAtom.categoryId]);

  // --- Errors ---
  if (isError && error.code === "ERR_NETWORK") return <NetworkError error={error} />;

  return (
    <div className="px-4 ">
      {/* Header with QR + title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 w-full my-4">
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="self-center sm:self-auto shrink-0"
          title="Open this form"
        >
          <div className="bg-white p-2 rounded shadow inline-block">
            <QRCode value={currentUrl} size={80} />
          </div>
        </a>
        <div className="text-center sm:text-left">
          <p className="uppercase font-semibold text-gray-800">{pageTitle}</p>
          <p className="text-xs text-gray-600 mt-1">Scan this to fill this form</p>
          
        </div>
      </div>

      <TermsAndConditions formTypeId={formId} />

      {isSuccess && userSections.length > 0 && (
        <FormLayout>
          <div className="flex flex-col space-y-12 ">
           {userSections.map((fs) => (
  <section key={fs.name ?? "sec"}>
    {fs.name && (
      <p className="text-center py-1.5 mb-4 uppercase text-gray-800 bg-gray-200">
        {fs.name}
      </p>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {fs.formFields.map((ff) => {
        const idStr = String(ff.formFieldId);       // ✅ always string
        const isCalculated = !!ff.isCalculated;
        const isImage = !!ff.isImage;

        const val = isCalculated
          ? String(computedNumbersById[idStr] ?? 0)
          : String(rawById.get(idStr) ?? "");       // ✅ read from Map

        return (
          <TextField
            key={idStr}
            id={idStr}
            label={ff.label}
            type={isCalculated ? "calculated" : ff.formFieldType?.fieldTypeName}
            required={ff.required}
            hidden={!ff.activeStatus}
            options={ff.formFieldOptions}
            onChange={isCalculated ? undefined : handleChange}
            disabled={isCalculated}
            value={val}
            isImage={isImage}
            imageSize={ff.imageSize}
          />
        );
      })}
    </div>
  </section>
))}

          </div>

          {isLoadedNotes &&
            notes?.filter((n) => n.activeStatus).map((note) => (
              <div key={note.noteId} className="mt-2">
                <p className="uppercase font-bold py-1 text-sm text-[#0A0D36]"> Note: </p>
                <div className="p-1.5 rounded bg-gray-50 text-sm">{note.text}</div>
              </div>
            ))}
        </FormLayout>
      )}

      {isSuccess && userSections.length === 0 && (
        <p className="text-center">
          Provided sections are for official use only. Kindly contact management for assistance.
        </p>
      )}

      {isLoading && <Loader className="fixed inset-0" />}

      {!allSections.length && (
        <div className="flex flex-col items-center mt-16">
          <NoData className="w-64 h-64 text-[#0A0D36]" />
          <p className="p-4 font-medium text-lg text-gray-600">
            There are no fields provided for this form. Kindly contact management.
          </p>
          <button
            type="button"
            className="block w-96 p-1.5 text-white bg-[#0A0D36] inline-flex justify-center items-center space-x-2"
            onClick={() => paperlessNavigate({ destination: "/", payload: "" })}
          >
            <MdKeyboardBackspace />
            <span> Home </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FormView;
